import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ApiClientError,
  api,
  tokenStore,
  type Module,
  type SessionUser,
} from "../lib/api";

interface AuthState {
  /**
   * `error` is distinct from `signedOut` on purpose. Treating every /auth/me
   * failure as "your token is bad" hid a real incident: a pending migration
   * made /auth/me return 500, the token was discarded, and sign-in silently
   * bounced back to the login screen with nothing shown. A server problem is
   * not an authentication problem and must not look like one.
   */
  status: "loading" | "signedOut" | "signedIn" | "error";
  user?: SessionUser;
  modules: Module[];
  /** True while a bootstrap password still has to be replaced. */
  mustChangePassword: boolean;
  errorMessage?: string;
}

interface AuthContextValue extends AuthState {
  signIn: (token: string) => Promise<void>;
  signOut: () => void;
  /** Re-reads /auth/me, e.g. after a password change clears the block. */
  refresh: () => Promise<void>;
  can: (module: Module) => boolean;
  /**
   * Does the signed-in person hold ANY of these roles?
   *
   * The replacement for `LIST.includes(user.role)`, which every screen was
   * doing before roles became many-to-many. That test reads the PRIMARY role,
   * so it hid a control from anybody whose relevant role was not the one the
   * server picked as primary - a Service Technician who also collects
   * batteries could not see the collection step.
   *
   * For hiding controls only. The API re-checks every request (REQ-NFR-4),
   * against the union of the roles, which is why this can be generous
   * without being a security boundary.
   */
  hasRole: (...roles: readonly string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    modules: [],
    mustChangePassword: false,
  });

  const load = useCallback(async () => {
    if (!tokenStore.get()) {
      setState({ status: "signedOut", modules: [], mustChangePassword: false });
      return;
    }
    try {
      const { user, modules, mustChangePassword } = await api.me();
      setState({ status: "signedIn", user, modules, mustChangePassword });
    } catch (caught) {
      const status = caught instanceof ApiClientError ? caught.status : 0;

      // Only these mean the credential itself is no longer good: expired or
      // forged token (401), or a deactivated account (403). Discard it and
      // ask for a fresh sign-in.
      if (status === 401 || status === 403) {
        tokenStore.clear();
        setState({ status: "signedOut", modules: [], mustChangePassword: false });
        return;
      }

      // Anything else - a 500, a 503, no network - is the server's problem.
      // Keep the token, say what happened, and offer a retry.
      setState({
        status: "error",
        modules: [],
        mustChangePassword: false,
        errorMessage:
          caught instanceof ApiClientError
            ? caught.message
            : "Could not reach the server.",
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback(
    async (token: string) => {
      tokenStore.set(token);
      await load();
    },
    [load],
  );

  const signOut = useCallback(() => {
    tokenStore.clear();
    setState({ status: "signedOut", modules: [], mustChangePassword: false });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      signOut,
      refresh: load,
      // Convenience for hiding navigation. Never a security boundary - the
      // API checks every request (REQ-NFR-4).
      can: (module: Module) => state.modules.includes(module),
      hasRole: (...roles: readonly string[]) =>
        (state.user?.roles ?? []).some((role) => roles.includes(role)),
    }),
    [state, signIn, signOut, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
